import angular from 'angular'

import cssParse from 'css/lib/parse'
import CssCompiler from 'css/lib/stringify/identity'

import {
  wrap, first, toArray, isArray,
  includes, isString, castArray,
  startsWith, escapeRegExp
} from 'lodash-es'

import { defaultTo } from './bits'
import { addSetToArray } from './arrayh'

import extractRawElm from './extract-raw-elm'

const cssCompiler = new CssCompiler()

angular
  .module('ggs.utils')
  .factory('domh', domhFactory)

function domhFactory(
  $log,
  rootId,
  rootQs,
  rootClass,
  addIdPrefix,
  $rootElement,
  $portalElement) {

  // getDocumentHeight

  function getDocumentHeight() {
    return document.body.offsetHeight
  }

  // getDocumentWidth

  function getDocumentWidth() {
    return document.body.offsetWidth
  }

  // isRootInPage

  function isRootInPage() {
    const rootElm = extractRawElm($rootElement)
    return document.body.contains(rootElm)
  }

  // isDisplayed

  function isDisplayed($elm, $baseElms = [$rootElement, $portalElement]) {
    const elm = extractRawElm($elm)
    const baseElms = flattenElms($baseElms)
    const style = window.getComputedStyle(elm)
    if (style.display === 'none') {
      return false
    } else if (!!elm.parentNode && (elm.parentNode !== document) && !includes(baseElms, elm.parentNode)) {
      return isDisplayed(elm.parentNode, baseElms)
    } else {
      return true
    }
  }

  // find

  find.notStrict = findNotStrict

  find.all = findAll
  find.all.notStrict = findAllNotStrict

  find.byId = findById
  find.byId.notStrict = findByIdNotStrict

  find.bySrc = findBySrc
  find.bySrc.notStrict = findBySrcNotStrict

  find.byClass = findByClass
  find.byClass.notStrict = findByClassNotStrict

  function find($baseElms, qs) {
    return findNotStrict($baseElms, qs)
  }

  function findNotStrict($baseElms, qs) {
    if (!isString(qs)) {
      qs = $baseElms
      $baseElms = [$rootElement, $portalElement]
    }

    const baseElms = flattenElms($baseElms)
    const queryResults = []
    for (const baseElm of baseElms) {
      const queryResult = baseElm.querySelector(qs)
      if (queryResult) queryResults.push(queryResult)
    }

    if (queryResults.length > 1) {
      $log.warn('domh#findNotStrict: more than one baseElm contains element that matches provided QS!', baseElms, qs, queryResults)
    }

    return defaultTo(first(queryResults), null)
  }

  function findAll($baseElms, qs) {
    return findAllNotStrict($baseElms, qs)
  }

  function findAllNotStrict($baseElms, qs) {
    if (!isString(qs)) {
      qs = $baseElms
      $baseElms = [$rootElement, $portalElement]
    }

    const baseElms = flattenElms($baseElms)
    const queryResults = []
    for (const baseElm of baseElms) {
      const queryResult = baseElm.querySelectorAll(qs)
      if (queryResult.length !== 0) queryResults.push(toArray(queryResult))
    }

    if (queryResults.length > 1) {
      $log.warn('domh#findAllNotStrict: more than one baseElm contains elements that matches provided QS!', baseElms, qs, queryResults)
    }

    return defaultTo(first(queryResults), [])
  }

  function findById($baseElms, id) {
    const elm = findByIdNotStrict($baseElms, id)
    return elm
  }

  function findByIdNotStrict($baseElms, id) {
    if (!isString(id)) {
      id = $baseElms
      $baseElms = [$rootElement, $portalElement]
    }

    const baseElms = flattenElms($baseElms)
    const queryResults = []
    for (const baseElm of baseElms) {
      const rootNode = baseElm.getRootNode()
      const isRootAttached = !!rootNode.getElementById
      const queryResult = isRootAttached
        ? rootNode.getElementById(id)
        : findNotStrict(`#${id}`, baseElm)
      if (queryResult && !includes(queryResults, queryResult)) queryResults.push(queryResult)
    }

    if (queryResults.length > 1) {
      $log.warn('domh#findByIdNotStrict: more than one baseElm contains elements that matches provided QS!', baseElms, id, queryResults)
    }

    return defaultTo(first(queryResults), null)
  }

  function findBySrc($baseElms, src) {
    const elm = findBySrcNotStrict($baseElms, src)
    return elm
  }

  function findBySrcNotStrict($baseElms, src) {
    if (!isString(src)) {
      src = $baseElms
      $baseElms = document
    }

    const elm = findNotStrict($baseElms, `[src="${src}"]`)
    return defaultTo(elm, null)
  }

  function findByClass($baseElms, className) {
    const elm = findByClassNotStrict($baseElms, className)
    return elm
  }

  function findByClassNotStrict($baseElms, className) {
    if (!isString(className)) {
      className = $baseElms
      $baseElms = [$rootElement, $portalElement]
    }

    const elm = findNotStrict($baseElms, `.${className}`)
    return defaultTo(elm, null)
  }

  // has

  has.byId = hasById
  has.bySrc = hasBySrc
  has.byClass = hasByClass

  function has($baseElms, qs) {
    const elm = findNotStrict($baseElms, qs)
    return !!elm
  }

  function hasById($baseElms, qs) {
    const elm = findByIdNotStrict($baseElms, qs)
    return !!elm
  }

  function hasBySrc($baseElms, qs) {
    const elm = findBySrcNotStrict($baseElms, qs)
    return !!elm
  }

  function hasByClass($baseElms, qs) {
    const elm = findByClassNotStrict($baseElms, qs)
    return !!elm
  }

  // flattenElms (private)

  function flattenElms($elms) {
    if (isArray($elms)) {
      const res = []
      for (const $elm of $elms) {
        const flat = flattenElms($elm)
        addSetToArray(res, flat)
      }
      return res
    } else {
      return castArray(extractRawElm($elms))
    }
  }

  // remove

  remove.notStrict = removeNotStrict

  remove.byId = removeById
  remove.byId.notStrict = removeByIdNotStrict

  function remove($elm) {
    const elm = extractRawElm($elm)
    const prtElm = elm.parentNode
    prtElm.removeChild(elm)
    return $elm
  }

  function removeNotStrict($elm) {
    const elm = extractRawElm($elm)
    const prtElm = elm.parentNode
    if (prtElm) prtElm.removeChild(elm)
    return $elm
  }

  function removeById(id) {
    const elm = findById(id)
    remove(elm)
    return id
  }

  function removeByIdNotStrict(id) {
    const elm = findByIdNotStrict(id)
    if (elm) removeNotStrict(elm)
    return id
  }

  // createHolder

  function createHolder(id, $prtElm = $rootElement) {
    const holder = document.createElement('div')
    holder.setAttribute('ggs-holder', true)
    holder.id = id
    appendTo(holder, $prtElm)
    return holder
  }

  // appendTo (private)

  const bodyHolderElm = createHolder(addIdPrefix('domhBody'), document.body)
  const rootHolderElm = createHolder(addIdPrefix('domhRoot'))

  function appendToBody($elm) {
    return appendTo($elm, bodyHolderElm)
  }

  function appendToRoot($elm) {
    return appendTo($elm, rootHolderElm)
  }

  function appendTo($elm, $prtElm) {
    const elm = extractRawElm($elm)
    const prtElm = extractRawElm($prtElm)
    prtElm.appendChild(elm)
    return $elm
  }

  // strictSrc

  function strictSrc(script, src) {
    script.src = src
    return script
  }

  // createLink

  function createLink(id) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.id = id

    link.attach = wrap(link, appendToBody)
    link.detach = wrap(link, removeNotStrict)
    return link
  }

  // createScript

  function createScript(id) {
    const script = document.createElement('script')
    script.async = true
    script.type = 'text/javascript'
    script.id = id

    script.attach = wrap(script, appendToBody)
    script.detach = wrap(script, removeNotStrict)
    script.strictSrc = wrap(script, strictSrc)
    return script
  }

  // createStyle

  function createStyle(id) {
    const style = document.createElement('style')
    style.type = 'text/css'
    style.id = id
    style.appendChild(document.createTextNode(''))

    style.clear = wrap(style, clearStyle)
    style.attach = wrap(style, appendToRoot)
    style.detach = wrap(style, removeNotStrict)

    style.local = wrap(style, setLocalStyle)
    style.local.important = wrap(style, setLocalImportantStyle)

    style.global = wrap(style, setGlobalStyle)
    style.global.important = wrap(style, setGlobalImportantStyle)

    return style
  }

  function clearStyle($style) {
    const style = extractRawElm($style)
    for (let index = style.childNodes.length - 1; index !== -1; index--) {
      const childNode = style.childNodes[index]
      style.removeChild(childNode)
    }
    return $style
  }

  function setLocalStyle($style, cssText) {
    return setGlobalStyle($style, cssText)
  }

  function setLocalImportantStyle($style, cssText) {
    const importantCssRules = castCssToImportant(cssText)
    return setLocalStyle($style, importantCssRules)
  }

  function setGlobalStyle($style, cssText) {
    const style = extractRawElm($style)
    clearStyle(style)
    style.appendChild(document.createTextNode(cssText))
    return $style
  }

  function setGlobalImportantStyle($style, cssText) {
    const importantCssRules = castCssToImportant(cssText)
    return setGlobalStyle($style, importantCssRules)
  }

  // castCssToImportant

  function castCssToImportant(cssText) {
    const importantCssRules = cssText.replace(/(!\s*important\s*)?;\B/ig, ' !important;')
    return importantCssRules
  }

  // hasRootQs

  const HAS_ROOT_SKIP_RE = /allowed to skip.+rootQs/

  function hasRootQs(cssText) {
    if (HAS_ROOT_SKIP_RE.test(cssText)) {
      return true
    }

    const cssAST = cssParse(cssText)
    const result = hasRootQsWalk(cssAST.stylesheet.rules)
    if (result === 0) {
      $log.warn('domh#hasRootQs: given cssText has zero selectors!', cssText)
    }
    return result !== false
  }

  function hasRootQsWalk(rules) {
    let count = 0
    for (const rule of rules) {
      if (rule.selectors) {
        for (const selector of rule.selectors) {
          if (!startsWith(selector, rootQs)) {
            return false
          }
          count++
        }
      }
      if (rule.rules) {
        const subResult = hasRootQsWalk(rule.rules)
        if (subResult === false) {
          return false
        }
        count += subResult
      }
    }
    return count
  }

  // addRootQs

  const ROOT_ID_RE = RegExp(escapeRegExp(`#${rootId}`), 'g')
  const ROOT_CLASS_RE = RegExp(escapeRegExp(`.${rootClass}`), 'g')

  function addRootQs(cssText) {
    const cssAST = cssParse(cssText)
    const count = addRootQsWalk(cssAST.stylesheet.rules)
    if (count === 0) {
      $log.warn('domh#addRootQs: given cssText has zero selectors!', cssText)
    }

    const body = cssCompiler.compile(cssAST)
    const head = `/* ${__filename}: selector has been prefixed with ${rootQs} by domh#addRootQs */\n\n`
    return head + body
  }

  function addRootQsWalk(rules) {
    let count = 0
    for (const rule of rules) {
      if (rule.selectors) {
        rule.selectors = rule.selectors.map((selector) => {
          return rootQs + ' ' + selector.replace(ROOT_ID_RE, '').replace(ROOT_CLASS_RE, '').trim()
        })
        count += rule.selectors.length
      }
      if (rule.rules) {
        count += addRootQsWalk(rule.rules)
      }
    }
    return count
  }

  // checkCssBrackets

  function checkCssBrackets(cssText) {
    const cssAST = cssParse(cssText, { silent: true })
    const result = cssAST.stylesheet.parsingErrors.length === 0
    return result
  }

  // exports

  return {
    getDocumentWidth,
    getDocumentHeight,

    isRootInPage,

    isDisplayed,

    find,
    has,
    remove,

    createHolder,

    createLink,
    createScript,
    createStyle,

    castCssToImportant,
    hasRootQs,
    addRootQs,
    checkCssBrackets
  }
}
